import {
    Component,
    HostListener,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    SimpleChanges,
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { forkJoin, Observable, of, Subscription, tap } from 'rxjs';
import { AuthenticationService } from 'src/app/authentication/services/authentication.service';
import { BrowserAndPhysicsLoadable } from 'src/app/object3-d/models/browser-and-physics-loadable';
import { SceneElement } from 'src/app/scenes/models/scene-element';
import { SceneService } from 'src/app/scenes/services/scene.service';
import { TrainingService } from 'src/app/shared/services/training.service';
import {
    BaseControllerConfiguration,
    ControllerConfiguration,
    ControllerConfigurationValueTypes,
    SceneElementActionConfigurationSpecification,
    SceneElementActionConfigurationUpdateDTO,
    ValidatorTypes,
} from '../interfaces/action-configuration';
import { AlgorithmValidator } from 'src/app/algorithm/validators/algorithm-validator';
import { BeforeTrainingStart } from '../../shared/interfaces/before-training-start';

@Component({
    selector: 'app-action-configurator',
    templateUrl: './action-configurator.component.html',
    styleUrls: ['./action-configurator.component.scss'],
    standalone: false,
})
export class ActionConfiguratorComponent
    extends BeforeTrainingStart
    implements OnInit, OnDestroy, OnChanges
{
    protected readonly ControllerConfigurationValueTypes =
        ControllerConfigurationValueTypes;

    @Input()
    editingIsDisabled: boolean;
    @Input()
    trainingId: string;

    specifications: SceneElementActionConfigurationSpecification[] = [];
    form: FormGroup;
    actionSpaceTypes: string[] = [];

    validationRuleToValidator = {
        [ValidatorTypes.REQUIRED]: Validators.required,
        [ValidatorTypes.VALIDATE_POSITIVE_DOUBLE]:
            AlgorithmValidator.validatePositiveFloat,
    };

    selectedElementIndex: number = 0;

    sceneElements = [
        {
            id: 'robotUuid',
            fixed: false,
            browserAndPhysicsLoadable: {
                name: 'robot',
            } as BrowserAndPhysicsLoadable,
        } as SceneElement,
    ];

    subscriptions: Subscription[] = [];

    constructor(
        private trainingService: TrainingService,
        private sceneService: SceneService,
        private authenticationService: AuthenticationService
    ) {
        super();
    }

    ngOnInit(): void {
        forkJoin([
            this.trainingService.getActionConfiguration(this.trainingId),
            this.sceneService.getScene(this.trainingId),
        ]).subscribe(([actionConfigurations, scene]) => {
            this.sceneElements = scene.sceneElements.filter(
                (element: SceneElement) =>
                    element.browserAndPhysicsLoadable.isRobot
            );
            this.form =
                actionConfigurations.length > 0
                    ? this.actionConfigurationFormFromSpecification(
                          actionConfigurations[this.selectedElementIndex]
                      )
                    : new FormGroup({});
            this.specifications = actionConfigurations;

            if (this.editingIsDisabled) this.form.disable();
        });

        this.subscriptions.push(
            this.authenticationService.logoutTriggered$.subscribe(() => {
                if (this.form.dirty) {
                    this.saveActionControllerConfiguration().subscribe(() => {
                        this.authenticationService.logoutPreparationsFinished$.next(
                            true
                        );
                    });
                } else {
                    this.authenticationService.logoutPreparationsFinished$.next(
                        true
                    );
                }
            })
        );
    }

    override saveBeforeStart(): Observable<any> {
        if (this.form.dirty) {
            return this.saveActionControllerConfiguration();
        } else return of(null);
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (
            changes.editingIsDisabled &&
            !changes.editingIsDisabled.isFirstChange()
        ) {
            if (this.editingIsDisabled) {
                this.form.disable();
            } else {
                this.form.enable();
            }
        }
    }

    actionConfigurationFormFromSpecification(
        actionConfiguration: SceneElementActionConfigurationSpecification
    ): FormGroup {
        this.actionSpaceTypes =
            actionConfiguration.actionSpaceConfiguration.availableConfigs;

        return new FormGroup({
            controllerConfigurations: new FormGroup(
                this.constructControllerConfigurationControlsFromSpecification(
                    actionConfiguration.controllerConfigurations
                )
            ),
            actionSpaceConfiguration: new FormControl(
                actionConfiguration.actionSpaceConfiguration.actionConfig
            ),
        });
    }

    constructControllerConfigurationControlsFromSpecification(
        controllerConfigurations: ControllerConfiguration[]
    ) {
        const prototype = {};
        controllerConfigurations.forEach(
            (controllerConfiguration: ControllerConfiguration) => {
                prototype[controllerConfiguration.name] = new FormControl(
                    '' + controllerConfiguration.value,
                    controllerConfiguration.validationRules.map(
                        (validationRule) =>
                            this.validationRuleToValidator[validationRule]
                    )
                );
            }
        );

        return prototype;
    }

    onSelectElement(index: number) {
        if (this.selectedElementIndex !== index && this.form.valid) {
            this.saveActionControllerConfiguration().subscribe();
            this.selectedElementIndex = index;
            this.form = this.actionConfigurationFormFromSpecification(
                this.specifications[this.selectedElementIndex]
            );
        }
    }

    saveActionControllerConfiguration(): Observable<
        SceneElementActionConfigurationUpdateDTO[]
    > {
        if (!this.trainingService) {
            return of(null);
        }
        this.updateSpecifications();
        return this.trainingService
            .updateActionConfiguration(
                this.trainingId,
                this.convertSpecificationToUpdateDTO()
            )
            .pipe(tap(() => this.form.markAsPristine()));
    }

    updateSpecifications() {
        this.specifications[
            this.selectedElementIndex
        ].actionSpaceConfiguration.actionConfig = this.form.get(
            'actionSpaceConfiguration'
        ).value;

        this.specifications[
            this.selectedElementIndex
        ].controllerConfigurations.forEach(
            (controllerConfiguration: ControllerConfiguration) => {
                const newValue = this.form
                    .get('controllerConfigurations')
                    .get(controllerConfiguration.name).value;
                controllerConfiguration.value =
                    controllerConfiguration.type ===
                    ControllerConfigurationValueTypes.STRING
                        ? newValue
                        : +newValue.replace(',', '.');
            }
        );
    }

    convertSpecificationToUpdateDTO(): SceneElementActionConfigurationUpdateDTO[] {
        return this.specifications.map(
            (specification: SceneElementActionConfigurationSpecification) => {
                return {
                    sceneElementId: specification.sceneElementId,
                    browserAndPhysicsLoadableId:
                        specification.browserAndPhysicsLoadableId,
                    actionSpaceConfiguration:
                        specification.actionSpaceConfiguration.actionConfig,
                    controllerConfigurations:
                        this.convertControllerConfigurationsToBaseControllerConfigurations(
                            specification.controllerConfigurations
                        ),
                } as SceneElementActionConfigurationUpdateDTO;
            }
        );
    }

    convertControllerConfigurationsToBaseControllerConfigurations(
        controllerConfigurations: ControllerConfiguration[]
    ): BaseControllerConfiguration[] {
        return controllerConfigurations.map(
            (controllerConfiguration: ControllerConfiguration) => {
                return {
                    name: controllerConfiguration.name,
                    value: controllerConfiguration.value,
                } as BaseControllerConfiguration;
            }
        );
    }

    ngOnDestroy(): void {
        if (this.form.dirty) {
            this.saveActionControllerConfiguration().subscribe();
        }
        this.subscriptions.forEach((subscription: Subscription) => {
            subscription.unsubscribe();
        });
    }

    @HostListener('window:beforeunload', ['$event'])
    beforeUnloadHandler(event) {
        if (this.form.dirty) {
            this.saveActionControllerConfiguration().subscribe();
            event.preventDefault();
            event.returnValue = false;
        }
    }
}
